import {Field, Form, Formik} from 'formik';
import React from 'react';
import PhoneInput, {isValidPhoneNumber} from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import * as Yup from 'yup';
import {Button, ButtonSize, ButtonType, ButtonVariant} from '../../components/button';
import {ButtonLoader} from '../../components/button-loader';
import {FormControl} from '../../components/form-control';
import {Input} from '../../components/input';
import {PhoneNumber} from '../../models';
import {convertPhoneNumberToString, convertStringToPhoneNumber} from '../../utils/phone-number.utils';

export interface PersonalInfoFormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: PhoneNumber;
  jobTitle: string;
  location: string;
  bio: string;
}

interface PersonalInfoFormProps {
  initialValues?: PersonalInfoFormData;
  onSubmit: (values: PersonalInfoFormData) => Promise<void>;
  onCancel: (resetForm: () => void) => void;
}

const personalInfoValidationSchema = Yup.object().shape({
  firstName: Yup.string().min(2, 'First name must be at least 2 characters').required('First name is required'),
  lastName: Yup.string().min(2, 'Last name must be at least 2 characters').required('Last name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  phoneNumber: Yup.object()
    .shape({
      countryCode: Yup.string(),
      phoneNumber: Yup.string(),
    })
    .test('is-valid-phone', 'Please enter a valid phone number', function (value) {
      if (!value || (!value.countryCode && !value.phoneNumber)) return true; // Optional field
      const fullNumber = `${value.countryCode}${value.phoneNumber}`;
      return fullNumber ? isValidPhoneNumber(fullNumber) : true;
    }),
  jobTitle: Yup.string(),
  location: Yup.string(),
  bio: Yup.string().max(500, 'Bio must be less than 500 characters'),
});

const defaultInitialValues: PersonalInfoFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: new PhoneNumber({countryCode: '', phoneNumber: ''}),
  jobTitle: '',
  location: '',
  bio: '',
};

const getTextareaClassName = (hasError: boolean) => {
  const baseClasses =
    'w-full px-4 py-3 border rounded-lg font-inherit bg-white transition-all duration-150 ease-in-out outline-none placeholder:text-gray-500 placeholder:opacity-100 resize-none';
  if (hasError) {
    return `${baseClasses} border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200`;
  }
  return `${baseClasses} border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`;
};

const getEmailFieldClassName = (hasEmail: boolean) => {
  if (hasEmail) {
    return 'bg-gray-100 cursor-not-allowed';
  }
  return '';
};

const isPhoneNumberPresent = (phoneNumber: PhoneNumber | undefined) => {
  return phoneNumber && (phoneNumber.countryCode || phoneNumber.phoneNumber);
};

const handlePhoneNumberChange = (value: string | undefined, setFieldValue: any) => {
  if (value && value.length > 16) {
    return;
  }
  setFieldValue('phoneNumber', convertStringToPhoneNumber(value || ''));
};

const getPhoneInputStyles = () =>
  ({
    '--PhoneInputCountryFlag-height': '1.25em',
    '--PhoneInputCountryFlag-width': '1.75em',
    '--PhoneInput-color--focus': '#d1d5db',
  } as React.CSSProperties);

const renderPhoneNumberField = (hasPhoneNumber: boolean, values: PersonalInfoFormData, setFieldValue: any) => {
  if (hasPhoneNumber) {
    return (
      <Input
        type="text"
        value={convertPhoneNumberToString(values.phoneNumber)}
        disabled={true}
        className="bg-gray-100 cursor-not-allowed"
      />
    );
  }

  return (
    <PhoneInput
      placeholder="Enter your Phone number"
      value={convertPhoneNumberToString(values.phoneNumber)}
      onChange={value => handlePhoneNumberChange(value, setFieldValue)}
      defaultCountry="US"
      limitMaxLength={true}
      className="w-full px-4 py-3 border rounded-lg font-inherit bg-white transition-all duration-150 ease-in-out outline-none placeholder:text-gray-500 placeholder:opacity-100 border-gray-300 focus:border-gray-300"
      style={getPhoneInputStyles()}
    />
  );
};

export const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({initialValues = defaultInitialValues, onSubmit, onCancel}) => {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-medium text-gray-900 mb-2">Personal Info</h2>
        <p className="text-gray-600">Make changes to your account here. Click save when you&apos;re done.</p>
      </div>

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={personalInfoValidationSchema}
        onSubmit={async (values, {setSubmitting}) => {
          try {
            await onSubmit(values);
          } finally {
            setSubmitting(false);
          }
        }}>
        {({values, errors, touched, isSubmitting, setFieldValue, resetForm}) => {
          const hasEmail = !!(values.email && values.email.trim() !== '');
          const hasPhoneNumber = isPhoneNumberPresent(initialValues.phoneNumber);

          return (
            <Form className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <FormControl label="First Name" error={touched.firstName ? errors.firstName : undefined} required>
                  <Field name="firstName">
                    {({field}: any) => <Input {...field} type="text" error={!!(touched.firstName && errors.firstName)} />}
                  </Field>
                </FormControl>

                <FormControl label="Last Name" error={touched.lastName ? errors.lastName : undefined} required>
                  <Field name="lastName">
                    {({field}: any) => <Input {...field} type="text" error={!!(touched.lastName && errors.lastName)} />}
                  </Field>
                </FormControl>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <FormControl label="Email" error={touched.email ? errors.email : undefined} required>
                  <Field name="email">
                    {({field}: any) => (
                      <Input
                        {...field}
                        type="email"
                        disabled={hasEmail}
                        error={!!(touched.email && errors.email)}
                        className={getEmailFieldClassName(hasEmail)}
                      />
                    )}
                  </Field>
                </FormControl>

                <FormControl label="Phone Number" error={touched.phoneNumber ? (errors.phoneNumber as string) : undefined}>
                  {renderPhoneNumberField(!!hasPhoneNumber, values, setFieldValue)}
                </FormControl>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <FormControl label="Job Title" error={touched.jobTitle ? errors.jobTitle : undefined}>
                  <Field name="jobTitle">
                    {({field}: any) => (
                      <Input {...field} type="text" placeholder="Add your job title" error={!!(touched.jobTitle && errors.jobTitle)} />
                    )}
                  </Field>
                </FormControl>

                <FormControl label="Location" error={touched.location ? errors.location : undefined}>
                  <Field name="location">
                    {({field}: any) => (
                      <Input {...field} type="text" placeholder="Add your Location" error={!!(touched.location && errors.location)} />
                    )}
                  </Field>
                </FormControl>
              </div>

              <FormControl label="Your Bio" error={touched.bio ? errors.bio : undefined}>
                <Field name="bio">
                  {({field}: any) => (
                    <textarea
                      {...field}
                      rows={4}
                      placeholder="Share your recruiting experience, specializations, and what motivates you to connect great talent with innovative companies..."
                      className={getTextareaClassName(!!(touched.bio && errors.bio))}
                    />
                  )}
                </Field>
              </FormControl>

              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  type={ButtonType.BUTTON}
                  variant={ButtonVariant.Secondary}
                  size={ButtonSize.MD}
                  onClick={() => onCancel(resetForm)}
                  disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type={ButtonType.SUBMIT} variant={ButtonVariant.Primary} size={ButtonSize.MD} disabled={isSubmitting}>
                  {isSubmitting ? <ButtonLoader /> : 'Save'}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};
